import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Brush, Check, ChevronsUpDown } from 'lucide-react'
import { useSupabaseClient } from 'src/api/supabase'
import { queryKeys } from 'src/api/db/queryKeys'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'src/shared/components/ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'src/shared/components/ui/command'
import { cn } from 'src/shared/components/ui/utils'
import { PageContainer } from 'src/shared/components/PageContainer'

import type { DecorationInfo_ } from 'src/api/db/apiDecorationInfo'
import { DecorationInfoTable } from '../Decorations/components/DecorationInfoTable'

const invalidateDecorations = (
  queryClient: ReturnType<typeof useQueryClient>
) => queryClient.invalidateQueries({ queryKey: [queryKeys.decorations] })

const PAGE_SIZE = 1000
const NONE = '__none__'

async function fetchAllPaginated<T>(
  fetchPage: (
    from: number,
    limit: number
  ) => Promise<{ data: T[] | null; error: unknown }>
): Promise<T[]> {
  const result: T[] = []
  let from = 0
  let hasMore = true
  while (hasMore) {
    const { data, error } = await fetchPage(from, PAGE_SIZE)
    if (error) throw error
    const rows = data ?? []
    result.push(...rows)
    hasMore = rows.length >= PAGE_SIZE
    from += PAGE_SIZE
  }
  return result
}

const fetchPrintCodeVendorPairs = async (
  supabase: ReturnType<typeof useSupabaseClient>,
  table: 'decoration_setup_cost' | 'decoration_unit_cost'
) => {
  const rows = await fetchAllPaginated<{
    print_code: string | null
    vendor: string | null
  }>(async (from, limit) =>
    supabase
      .from(table)
      .select('print_code, vendor')
      .not('print_code', 'is', null)
      .order('print_code')
      .range(from, from + limit - 1)
  )
  const codes = new Set<string>()
  const vendorMap = new Map<string, string>()
  for (const row of rows) {
    const pc = row.print_code?.trim()
    if (pc) {
      codes.add(pc)
      if (row.vendor?.trim() && !vendorMap.has(pc))
        vendorMap.set(pc, row.vendor.trim())
    }
  }
  return { codes, vendorMap }
}

export const DecorationsInfoPage = () => {
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()

  const [infoIdFilter, setInfoIdFilter] = useState<number | null>(null)
  const [printCodeFilter, setPrintCodeFilter] = useState('')

  const { data: printCodeData } = useQuery({
    queryKey: [queryKeys.decorations, 'info', 'allPrintCodes'],
    queryFn: async () => {
      const [setup, unit] = await Promise.all([
        fetchPrintCodeVendorPairs(supabase, 'decoration_setup_cost'),
        fetchPrintCodeVendorPairs(supabase, 'decoration_unit_cost'),
      ])
      const union = new Set([...setup.codes, ...unit.codes])
      const vendorMap = new Map([...setup.vendorMap, ...unit.vendorMap])
      const allPrintCodes = [...union].sort().filter(Boolean)
      return { allPrintCodes, vendorMap }
    },
  })

  const allPrintCodes = printCodeData?.allPrintCodes
  const vendorMap = printCodeData?.vendorMap

  const { data: decorationInfos } = useQuery({
    queryKey: [queryKeys.decorations, 'info', 'printCodesWithInfo'],
    queryFn: async () =>
      fetchAllPaginated<DecorationInfo_>(async (from, limit) =>
        supabase
          .from('decoration_info')
          .select('*')
          .not('print_codes', 'is', null)
          .order('id', { ascending: true })
          .range(from, from + limit - 1)
      ),
  })

  const printCodesWithoutInfo = useMemo(() => {
    if (!allPrintCodes || !decorationInfos) return []

    const printCodesWithInfoSet = new Set(
      decorationInfos.flatMap((di) => di.print_codes ?? [])
    )

    return allPrintCodes.filter((pc) => pc && !printCodesWithInfoSet.has(pc))
  }, [allPrintCodes, decorationInfos])

  const activeFilter = infoIdFilter
    ? { type: 'id' as const, value: infoIdFilter }
    : printCodeFilter.trim()
    ? { type: 'code' as const, value: printCodeFilter.trim() }
    : null

  const { data: infoList, isFetching: isInfoFetching } = useQuery({
    queryKey: [queryKeys.decorations, 'info', activeFilter],
    queryFn: async (): Promise<DecorationInfo_[]> =>
      fetchAllPaginated<DecorationInfo_>(async (from, limit) => {
        let q = supabase
          .from('decoration_info')
          .select('*')
          .order('id', { ascending: true })
          .range(from, from + limit - 1)
        if (activeFilter?.type === 'id') q = q.eq('id', activeFilter.value)
        else if (activeFilter?.type === 'code')
          q = q.contains('print_codes', [activeFilter.value])
        return await q
      }),
  })

  const createInfo = useMutation({
    mutationFn: async (insert: Record<string, unknown>) => {
      const { data, error } = await supabase
        .from('decoration_info')
        .insert(insert)
        .select('id')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => invalidateDecorations(queryClient),
  })

  const updateInfo = useMutation({
    mutationFn: async ({
      id,
      updates,
    }: {
      id: number
      updates: Record<string, unknown>
    }) => {
      const { error } = await supabase
        .from('decoration_info')
        .update(updates)
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => invalidateDecorations(queryClient),
  })

  const deleteInfo = useMutation({
    mutationFn: async (id: number) => {
      const { error } = await supabase
        .from('decoration_info')
        .delete()
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => invalidateDecorations(queryClient),
  })

  const handleInfoSelect = (v: string) => {
    const id = v === NONE ? null : Number(v)
    setInfoIdFilter(id)
    setPrintCodeFilter('')
  }

  const handlePrintCodeSelect = (v: string) => {
    setPrintCodeFilter(v === NONE ? '' : v)
    setInfoIdFilter(null)
  }

  const [infoOpen, setInfoOpen] = useState(false)
  const [printCodeOpen, setPrintCodeOpen] = useState(false)

  const infoItems = useMemo(
    () =>
      (decorationInfos ?? [])
        .filter(
          (di): di is DecorationInfo_ & { print_codes: string[] } =>
            Array.isArray(di.print_codes) && di.print_codes.length > 0
        )
        .map((di) => ({
          value: String(di.id),
          label: `${di.name}: ${di.print_codes.join(', ')}`,
          keywords: di.print_codes,
        })),
    [decorationInfos]
  )

  const printCodeItems = useMemo(
    () =>
      printCodesWithoutInfo.map((pc) => ({
        value: pc,
        label: vendorMap?.get(pc) ? `${vendorMap.get(pc)}: ${pc}` : pc,
      })),
    [printCodesWithoutInfo, vendorMap]
  )

  const selectedInfoLabel = infoIdFilter
    ? infoItems.find((i) => i.value === String(infoIdFilter))?.label
    : null

  const selectedPrintCodeLabel = printCodeFilter
    ? printCodeItems.find((i) => i.value === printCodeFilter)?.label
    : null

  return (
    <PageContainer width="xl">
      <Column className="gap-6">
        <Row className="gap-2 items-center">
          <Brush className="h-6 w-6" />
          <h1>Decorations info</h1>
        </Row>

        <Row className="gap-4 items-end flex-wrap">
          <Popover open={infoOpen} onOpenChange={setInfoOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="secondary"
                role="combobox"
                aria-expanded={infoOpen}
                className="w-[600px] justify-between font-normal"
              >
                <span className="truncate">
                  {selectedInfoLabel ?? 'Select decoration info'}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[600px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search decoration info..." />
                <CommandList>
                  <CommandEmpty>No decoration info found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value={NONE}
                      onSelect={() => {
                        handleInfoSelect(NONE)
                        setInfoOpen(false)
                      }}
                    >
                      Select decoration info
                      <Check
                        className={cn(
                          'ml-auto h-4 w-4',
                          !infoIdFilter ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                    {infoItems.map((item) => (
                      <CommandItem
                        key={item.value}
                        value={item.label}
                        keywords={item.keywords}
                        onSelect={() => {
                          handleInfoSelect(item.value)
                          setInfoOpen(false)
                        }}
                      >
                        {item.label}
                        <Check
                          className={cn(
                            'ml-auto h-4 w-4',
                            infoIdFilter === Number(item.value)
                              ? 'opacity-100'
                              : 'opacity-0'
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          <Popover open={printCodeOpen} onOpenChange={setPrintCodeOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="secondary"
                role="combobox"
                aria-expanded={printCodeOpen}
                className="w-[300px] justify-between font-normal"
              >
                <span className="truncate">
                  {selectedPrintCodeLabel ?? 'Select print code to add info'}
                </span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-0" align="start">
              <Command>
                <CommandInput placeholder="Search print code..." />
                <CommandList>
                  <CommandEmpty>No print code found.</CommandEmpty>
                  <CommandGroup>
                    <CommandItem
                      value={NONE}
                      onSelect={() => {
                        handlePrintCodeSelect(NONE)
                        setPrintCodeOpen(false)
                      }}
                    >
                      Select print code to add info
                      <Check
                        className={cn(
                          'ml-auto h-4 w-4',
                          !printCodeFilter ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                    {printCodeItems.map((item) => (
                      <CommandItem
                        key={item.value}
                        value={item.label}
                        onSelect={() => {
                          handlePrintCodeSelect(item.value)
                          setPrintCodeOpen(false)
                        }}
                      >
                        {item.label}
                        <Check
                          className={cn(
                            'ml-auto h-4 w-4',
                            printCodeFilter === item.value
                              ? 'opacity-100'
                              : 'opacity-0'
                          )}
                        />
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </Row>

        <Column className="gap-2">
          <DecorationInfoTable
            data={infoList ?? []}
            printCode={printCodeFilter.trim() || null}
            isLoading={isInfoFetching}
            onSave={async (updates) => {
              await Promise.all(
                updates.map(({ id, ...rest }) =>
                  updateInfo.mutateAsync({ id, updates: rest })
                )
              )
            }}
            onAdd={async (insert) => {
              await createInfo.mutateAsync(insert)
            }}
            onDelete={async (id) => {
              await deleteInfo.mutateAsync(id)
            }}
          />
        </Column>
      </Column>
    </PageContainer>
  )
}
